import React from 'react';
import { Radio, Checkbox, Button, Tag, Tooltip } from 'antd';
import { intentTypeIcons } from '../config.js';

const TYPE_COLORS = {
  EXPENSE: 'red',
  INCOME: 'green',
  TRANSFER: 'default',
  TAX: 'purple',
};

export const TYPE_OPTS = [
  { value: 'EXPENSE', label: 'Odhodek' },
  { value: 'INCOME', label: 'Prihodek' },
  { value: 'TRANSFER', label: 'Prenos' },
  { value: 'TAX', label: 'DDV' },
];

const bigButtonStyle = {
  width: 120,
  height: 120,
  margin: 8,
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: 16,
};

const iconStyle = { fontSize: 36, marginBottom: 8 };

const IntentTypeSelect = ({
  value,
  onChange,
  mode,
  disabled = false,
  small = false,
  hidden = [],
  // Individual options to grey out while the rest stay pickable. Used when
  // editing: a transaction can be flipped between expense and income, but not
  // turned into a transfer or a DDV row. Hiding them instead would make the
  // control lie about what a transfer already is.
  disabledOpts = [],
  disabledOptsReason,
  // Why the control as a whole is frozen. Distinct from disabledOptsReason,
  // which is about the individual options that cannot be picked.
  lockedReason,
}) => {
  const handleRadioChange = (e) => {
    onChange?.(e.target.value);
  };

  const handleCheckboxChange = (selectedValues) => {
    onChange?.(selectedValues);
  };

  const handleBigButtonClick = (val) => {
    if (mode === 'multi') {
      onChange?.([val]);
    } else {
      onChange?.(val);
    }
  };

  const isEmpty = mode === 'multi' ? !value || value.length === 0 : !value;

  const visibleOpts = TYPE_OPTS.filter(o => !hidden.includes(o.value));

  // If in small mode, skip big buttons entirely
  if (!small && isEmpty) {
    return (
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-around', width: '100%' }}>
        {visibleOpts.map(({ value: val, label }) => (
          <Button
            key={val}
            type="default"
            disabled={disabled}
            onClick={() => handleBigButtonClick(val)}
            style={bigButtonStyle}
            icon={React.cloneElement(intentTypeIcons[val], { style: iconStyle })}
          >
            {label}
          </Button>
        ))}
      </div>
    );
  }

  const selectableOpts = visibleOpts.map(({ value: val, label }) => {
    // The option the row already carries stays pickable whatever else is
    // barred, or the group would render with nothing selected.
    const optDisabled = disabledOpts.includes(val) && val !== value;
    const body = (
      <span style={{ whiteSpace: 'nowrap' }}>
        {React.cloneElement(intentTypeIcons[val], { style: { ...intentTypeIcons[val].props.style, marginRight: 6 } })}
        {label}
      </span>
    );
    return {
      value: val,
      disabled: optDisabled,
      label: optDisabled && disabledOptsReason
        ? <Tooltip title={disabledOptsReason}>{body}</Tooltip>
        : body,
    };
  });

  if (disabled && value && mode !== 'multi') {
    const opt = TYPE_OPTS.find(o => o.value === value);
    const tag = (
      <Tag color={TYPE_COLORS[value]} icon={intentTypeIcons[value]} style={{ fontSize: 14, padding: '4px 10px' }}>
        {opt?.label ?? value}
      </Tag>
    );
    // A frozen control that says nothing leaves the user guessing why.
    return lockedReason ? <Tooltip title={lockedReason}>{tag}</Tooltip> : tag;
  }

  // Otherwise, render the usual inputs
  return mode === 'multi' ? (
    <Checkbox.Group
      disabled={disabled}
      value={value}
      onChange={handleCheckboxChange}
      options={selectableOpts}
      size={small ? 'small' : 'middle'}
    />
  ) : (
    <Radio.Group
      disabled={disabled}
      value={value}
      onChange={handleRadioChange}
      options={selectableOpts}
      optionType="button"
      buttonStyle="solid"
      size={small ? 'small' : 'middle'}
    />
  );
};

export default IntentTypeSelect;
